import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Radio, Button, Upload, Input, Alert } from 'antd';
import { UploadOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../PersianDatePicker';
import { shiftsApi, attendanceApi, show422Error, ValidationError } from '../../../utils/api';
import { message } from 'antd';
import moment from 'moment-jalaali';
import { scrollToFirstError } from '../../../utils/formHelpers';

const { Option } = Select;
const { TextArea } = Input;

const GroupShiftAssignmentModal = ({ visible, onCancel, onSave, employees = [], departments = [] }) => {
  const [form] = Form.useForm();
  const [assignBy, setAssignBy] = useState('date');
  const [shiftsList, setShiftsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadShiftsList();
      form.setFieldsValue({
        assign_shift_by: 'date',
        send_email: 'no',
      });
    } else {
      form.resetFields();
      setSelectedDept(null);
      setFilteredEmployees([]);
    }
  }, [visible, form]);

  const loadShiftsList = async () => {
    setShiftsLoading(true);
    try {
      const res = await shiftsApi.getList();
      const shifts = res.data?.shifts || [];
      setShiftsList(shifts);
    } catch (error) {
      console.error('خطا در دریافت لیست شیفت‌ها:', error);
      message.error('خطا در دریافت لیست شیفت‌ها');
    } finally {
      setShiftsLoading(false);
    }
  };

  const handleDepartmentChange = async (deptId) => {
    setSelectedDept(deptId);
    form.setFieldsValue({ user_id: [] });
    
    if (deptId) {
      setEmployeesLoading(true);
      try {
        const res = await attendanceApi.getEmployeesByDepartment(deptId);
        const deptEmployees = res.data?.employees || res?.employees || [];
        setFilteredEmployees(deptEmployees);
      } catch (error) {
        console.error('خطا در دریافت کارمندان:', error);
        message.error('خطا در دریافت کارمندان دپارتمان');
        setFilteredEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    } else {
      setFilteredEmployees([]);
    }
  };

  // تابع تبدیل اعداد فارسی به انگلیسی
  const persianToEnglishDigits = (str) => {
    if (!str) return str;
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = String(str);
    persianDigits.forEach((persian, index) => {
      result = result.replace(new RegExp(persian, 'g'), englishDigits[index]);
    });
    return result;
  };

  // تابع برای فرمت تاریخ جلالی با اعداد انگلیسی
  const formatJalaliDate = (date) => {
    if (!date) return null;
    
    // اگر رشته است، اعداد فارسی را به انگلیسی تبدیل کن
    if (typeof date === 'string') {
      return persianToEnglishDigits(date);
    }
    
    // اگر DateObject از react-multi-date-picker است
    if (date.format && typeof date.format === 'function') {
      const formatted = date.format('YYYY/MM/DD');
      return persianToEnglishDigits(formatted);
    }
    
    // اگر DateObject با jYear, jMonth, jDay است
    if (date.jYear !== undefined && date.jMonth !== undefined && date.jDay !== undefined) {
      const jYear = date.jYear;
      const jMonth = String(date.jMonth + 1).padStart(2, '0');
      const jDay = String(date.jDay).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    // اگر moment object است
    if (date.jYear && typeof date.jYear === 'function') {
      const jYear = date.jYear();
      const jMonth = String(date.jMonth() + 1).padStart(2, '0');
      const jDay = String(date.jDate()).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    return null;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // ساخت payload بر اساس assign_shift_by
      const payload = {
        user_id: values.user_id || [],
        shift: values.shift ? Number(values.shift) : null,
        assign_shift_by: values.assign_shift_by,
        send_email: values.send_email || 'no',
        remarks: values.remarks || '',
      };

      // تنظیم فیلدهای مربوط به هر حالت
      if (values.assign_shift_by === 'date') {
        // حالت single-date
        const singleDate = values.single_date;
        payload.single_date = formatJalaliDate(singleDate);
        payload.multi_date = null;
        payload.month = null;
        payload.year = null;
      } else if (values.assign_shift_by === 'multiple') {
        // حالت multidate
        const dateRange = values.multi_date;
        let rangeStr = '';
        
        if (dateRange && Array.isArray(dateRange) && dateRange.length === 2) {
          const start = formatJalaliDate(dateRange[0]);
          const end = formatJalaliDate(dateRange[1]);
          rangeStr = `${start} - ${end}`;
        } else if (typeof dateRange === 'string') {
          rangeStr = persianToEnglishDigits(dateRange);
        }

        payload.single_date = null;
        payload.multi_date = rangeStr;
        payload.month = null;
        payload.year = null;
      } else if (values.assign_shift_by === 'month') {
        // حالت month
        payload.single_date = null;
        payload.multi_date = null;
        payload.month = String(values.month).padStart(2, '0');
        payload.year = String(values.year);
      }

      if (onSave) {
        await onSave(payload);
      }
      
      form.resetFields();
      setSelectedDept(null);
    } catch (error) {
      console.error('Validation failed:', error);
      
      // اگر خطای validation است، به اولین فیلد خطادار اسکرول کن
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      }
      
      // اگر خطای API است (422)
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else if (!error.errorFields) {
        // فقط اگر خطای validation نبود، پیام خطا نمایش بده
        message.error(error?.message || 'خطا در ثبت شیفت‌ها');
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
  };

  return (
    <Modal
      title={
        <div>
          <div className="text-xl font-bold" style={{ fontFamily: 'CustomFont' }}>
            اختصاص گروهی شیفت‌ها
          </div>
          <div className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'CustomFont' }}>
            افزودن برنامه شیفت
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      style={{ fontFamily: 'CustomFont' }}
    >
      <Alert
        message="شیفت موجود جایگزین خواهد شد."
        type="info"
        icon={<InfoCircleOutlined />}
        className="mb-4"
        style={{ fontFamily: 'CustomFont' }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ fontFamily: 'CustomFont' }}
        initialValues={{
          assign_shift_by: 'date',
          send_email: 'no',
        }}
      >
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>دپارتمان</span>}
            name="department"
          >
            <Select
              placeholder="انتخاب دپارتمان"
              style={{ fontFamily: 'CustomFont' }}
              onChange={handleDepartmentChange}
              allowClear
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={String(dept.id)}>
                  {dept.team_name || dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>کارمندان</span>}
            name="user_id"
            rules={[{ required: true, message: 'لطفاً کارمندان را انتخاب کنید' }]}
          >
            <Select
              mode="multiple"
              placeholder={
                !selectedDept
                  ? 'ابتدا دپارتمان را انتخاب کنید'
                  : employeesLoading
                    ? 'در حال بارگذاری...'
                    : 'انتخاب کارمندان'
              }
              style={{ fontFamily: 'CustomFont' }}
              loading={employeesLoading}
              disabled={!selectedDept}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (option?.value === '__all__') return false;
                return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
              }}
              dropdownRender={(menu) => (
                <>
                  {selectedDept && !employeesLoading && filteredEmployees.length > 0 && (
                    <div className="p-2 border-b border-gray-200 dark:border-gray-600" style={{ fontFamily: 'CustomFont' }}>
                      <Button
                        type="link"
                        size="small"
                        className="p-0 h-auto text-blue-600 dark:text-blue-400"
                        onClick={() => {
                          const allIds = filteredEmployees.map((e) => Number(e.id));
                          form.setFieldsValue({ user_id: allIds });
                        }}
                      >
                        انتخاب همه کارمندان این دپارتمان
                      </Button>
                    </div>
                  )}
                  {menu}
                </>
              )}
            >
              {filteredEmployees.map((emp) => (
                <Option key={emp.id} value={Number(emp.id)}>
                  {emp.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item
          label={<span style={{ fontFamily: 'CustomFont' }}>شیفت</span>}
          name="shift"
          rules={[{ required: true, message: 'لطفاً شیفت را انتخاب کنید' }]}
        >
          <Select
            placeholder="انتخاب شیفت"
            style={{ fontFamily: 'CustomFont' }}
            loading={shiftsLoading}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {shiftsList.map((shiftItem) => (
              <Option key={shiftItem.id} value={shiftItem.id}>
                <div className="flex items-center gap-2">
                  {shiftItem.color && (
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: shiftItem.color }}
                    />
                  )}
                  <span>
                    {shiftItem.shift_name} ({shiftItem.shift_short_code})
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={<span style={{ fontFamily: 'CustomFont' }}>اختصاص شیفت توسط</span>}
          name="assign_shift_by"
          rules={[{ required: true, message: 'لطفاً نوع اختصاص را انتخاب کنید' }]}
        >
          <Radio.Group
            onChange={(e) => setAssignBy(e.target.value)}
            style={{ fontFamily: 'CustomFont' }}
          >
            <Radio value="date">تاریخ واحد</Radio>
            <Radio value="multiple">بازه تاریخ</Radio>
            <Radio value="month">ماه</Radio>
          </Radio.Group>
        </Form.Item>

        {assignBy === 'date' && (
          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>}
            name="single_date"
            rules={[{ required: true, message: 'لطفاً تاریخ را انتخاب کنید' }]}
          >
            <PersianDatePicker
              placeholder="تاریخ را انتخاب کنید"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {assignBy === 'multiple' && (
          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>بازه تاریخ</span>}
            name="multi_date"
            rules={[{ required: true, message: 'لطفاً بازه تاریخ را انتخاب کنید' }]}
          >
            <PersianDatePicker
              range
              placeholder="انتخاب بازه تاریخ"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {assignBy === 'month' && (
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>سال</span>}
              name="year"
              rules={[{ required: true, message: 'لطفاً سال را انتخاب کنید' }]}
            >
              <Select placeholder="انتخاب سال" style={{ fontFamily: 'CustomFont' }}>
                <Option value="1404">۱۴۰۴</Option>
                <Option value="1403">۱۴۰۳</Option>
                <Option value="1402">۱۴۰۲</Option>
                <Option value="1405">۱۴۰۵</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>ماه</span>}
              name="month"
              rules={[{ required: true, message: 'لطفاً ماه را انتخاب کنید' }]}
            >
              <Select placeholder="انتخاب ماه" style={{ fontFamily: 'CustomFont' }}>
                <Option value="1">فروردین</Option>
                <Option value="2">اردیبهشت</Option>
                <Option value="3">خرداد</Option>
                <Option value="4">تیر</Option>
                <Option value="5">مرداد</Option>
                <Option value="6">شهریور</Option>
                <Option value="7">مهر</Option>
                <Option value="8">آبان</Option>
                <Option value="9">آذر</Option>
                <Option value="10">دی</Option>
                <Option value="11">بهمن</Option>
                <Option value="12">اسفند</Option>
              </Select>
            </Form.Item>
          </div>
        )}

        <Form.Item
          label={<span style={{ fontFamily: 'CustomFont' }}>ارسال ایمیل</span>}
          name="send_email"
        >
          <Radio.Group style={{ fontFamily: 'CustomFont' }}>
            <Radio value="yes">بله</Radio>
            <Radio value="no">خیر</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={<span style={{ fontFamily: 'CustomFont' }}>یادداشت</span>}
          name="remarks"
        >
          <TextArea
            rows={4}
            placeholder="یادداشت اختیاری..."
            style={{ fontFamily: 'CustomFont' }}
          />
        </Form.Item>

        <Form.Item
          label={
            <span>
              افزودن فایل{' '}
              <QuestionCircleOutlined className="text-gray-400" />
            </span>
          }
          name="file"
        >
          <Upload {...uploadProps} style={{ fontFamily: 'CustomFont' }}>
            <Button icon={<UploadOutlined />} style={{ fontFamily: 'CustomFont' }}>
              انتخاب فایل
            </Button>
          </Upload>
        </Form.Item>

        <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={onCancel}
            size="middle"
            style={{ fontFamily: 'CustomFont' }}
            disabled={loading}
          >
            لغو
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="middle"
            className="bg-green-600 hover:bg-green-700 border-green-600"
            style={{ fontFamily: 'CustomFont' }}
            loading={loading}
          >
            ذخیره
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default GroupShiftAssignmentModal;
